import initSqlJs, { Database, SqlJsStatic } from 'sql.js';
import type { FeatureCollection, Feature } from 'geojson';
import type { BoundingBox } from '../types/geospatial';

const SQLITE_WASM_PATH = '/sql-wasm.wasm';
const DB_STORAGE_KEY = 'wildfire_sqlite_db_v2';
const WASM_MAGIC = [0x00, 0x61, 0x73, 0x6d] as const;

let persistenceDisabled = false;
let quotaWarningLogged = false;

let sqlJsInstance: Promise<SqlJsStatic> | null = null;
let dbInstance: Promise<Database> | null = null;

function hasWasmMagic(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  return WASM_MAGIC.every((value, index) => bytes[index] === value);
}

function getWasmCandidateUrls(): string[] {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const fromPublic = publicUrl
    ? `${publicUrl}/sql-wasm.wasm`
    : SQLITE_WASM_PATH;

  return Array.from(
    new Set([
      `${fromPublic}?v=${Date.now()}`,
      `${SQLITE_WASM_PATH}?v=${Date.now()}`,
      '/static/js/sql-wasm.wasm',
      '/static/js/sql-wasm.wasm?v=1',
    ])
  );
}

async function fetchValidWasmBinary(): Promise<{ url: string; bytes: Uint8Array }> {
  const errors: string[] = [];

  for (const candidateUrl of getWasmCandidateUrls()) {
    try {
      const response = await fetch(candidateUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        errors.push(`${candidateUrl} -> HTTP ${response.status}`);
        continue;
      }

      const bytes = new Uint8Array(await response.arrayBuffer());
      if (!hasWasmMagic(bytes)) {
        const preview = Array.from(bytes.slice(0, 4))
          .map((value) => value.toString(16).padStart(2, '0'))
          .join(' ');
        errors.push(`${candidateUrl} -> invalid magic bytes: ${preview}`);
        continue;
      }

      return { url: candidateUrl, bytes };
    } catch (error) {
      errors.push(`${candidateUrl} -> ${String(error)}`);
    }
  }

  throw new Error(
    `Unable to load a valid sql.js WebAssembly binary. Attempts: ${errors.join(' | ')}`
  );
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch (error) {
    console.warn(
      'Local storage unavailable, database will be in-memory.',
      error
    );
    return null;
  }
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

async function loadSqlJs(): Promise<SqlJsStatic> {
  if (!sqlJsInstance) {
    sqlJsInstance = (async () => {
      const wasm = await fetchValidWasmBinary();
      return initSqlJs({
        locateFile: (file: string) => {
          if (file === 'sql-wasm.wasm') {
            return wasm.url;
          }
          return `/${file}`;
        },
        wasmBinary: wasm.bytes,
      });
    })();
  }
  return sqlJsInstance;
}

function ensureSchema(db: Database) {
  db.run(`
    CREATE TABLE IF NOT EXISTS observations (
      id INTEGER PRIMARY KEY AUTOINCREMENT,
      country_code TEXT NOT NULL,
      latitude REAL NOT NULL,
      longitude REAL NOT NULL,
      brightness REAL NOT NULL,
      scan REAL,
      track REAL,
      acq_date TEXT NOT NULL,
      acq_time TEXT NOT NULL,
      satellite TEXT NOT NULL,
      confidence INTEGER,
      version TEXT,
      bright_t31 REAL,
      frp REAL,
      daynight TEXT,
      UNIQUE(country_code, latitude, longitude, acq_date, acq_time, satellite)
    )
  `);
  db.run(`
    CREATE INDEX IF NOT EXISTS idx_observations_country_code
      ON observations(country_code)
  `);
}

function hydrateDatabase(SQL: SqlJsStatic): Database {
  const storage = getStorage();
  if (!storage) {
    const db = new SQL.Database();
    ensureSchema(db);
    return db;
  }
  const stored = storage.getItem(DB_STORAGE_KEY);
  if (stored) {
    try {
      const db = new SQL.Database(base64ToUint8Array(stored));
      ensureSchema(db);
      return db;
    } catch (error) {
      console.error(
        'Failed to restore SQLite database, creating a new one.',
        error
      );
      storage.removeItem(DB_STORAGE_KEY);
    }
  }
  const db = new SQL.Database();
  ensureSchema(db);
  return db;
}

async function getDatabase(): Promise<Database> {
  if (!dbInstance) {
    dbInstance = (async () => {
      const SQL = await loadSqlJs();
      return hydrateDatabase(SQL);
    })();
  }
  return dbInstance;
}

function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  if (typeof error === 'object') {
    const maybeDomException = error as Partial<DOMException> & {
      code?: number;
      name?: string;
    };
    const quotaErrorCodes = new Set([22, 1014]);
    if (maybeDomException.code && quotaErrorCodes.has(maybeDomException.code)) {
      return true;
    }
    if (maybeDomException.name) {
      return (
        maybeDomException.name === 'QuotaExceededError' ||
        maybeDomException.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      );
    }
  }
  return false;
}

function persistDatabase(db: Database) {
  if (persistenceDisabled) return;
  const storage = getStorage();
  if (!storage) return;
  const exported = db.export();
  try {
    storage.setItem(DB_STORAGE_KEY, uint8ArrayToBase64(exported));
  } catch (error) {
    if (isQuotaExceededError(error)) {
      persistenceDisabled = true;
      if (!quotaWarningLogged) {
        console.warn(
          'Local storage quota exceeded for wildfire observations. Persistence disabled for this session.',
          error
        );
        quotaWarningLogged = true;
      }
      try {
        storage.removeItem(DB_STORAGE_KEY);
      } catch (cleanupError) {
        console.debug(
          'Failed to remove stored wildfire observations after quota error.',
          cleanupError
        );
      }
      return;
    }
    throw error;
  }
}
export interface ObservationRecord {
  latitude: number;
  longitude: number;
  brightness: number;
  scan: number | null;
  track: number | null;
  acq_date: string;
  acq_time: string;
  satellite: string;
  confidence: number | null;
  version: string | null;
  bright_t31: number | null;
  frp: number | null;
  daynight: string | null;
}

interface NormalizedBounds {
  south: number;
  north: number;
  west: number;
  east: number;
}

function normalizeBounds(bounds: BoundingBox): NormalizedBounds {
  const north = Number(bounds.north);
  const south = Number(bounds.south);
  const east = Number(bounds.east);
  const west = Number(bounds.west);

  if (![north, south, east, west].every((value) => Number.isFinite(value))) {
    throw new Error('Bounding box values must be finite numbers.');
  }

  return {
    south: Math.min(north, south),
    north: Math.max(north, south),
    west: Math.min(east, west),
    east: Math.max(east, west),
  };
}

function extractCount(row: Record<string, unknown>): number {
  const value =
    row['count'] ?? row['COUNT'] ?? Object.values(row)[0];
  const count = Number(value);
  if (!Number.isFinite(count)) {
    throw new Error('Failed to parse count from query result.');
  }
  return count;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeObservation(
  raw: Record<string, any>
): ObservationRecord | null {
  const latitude = toNumber(raw.latitude);
  const longitude = toNumber(raw.longitude);
  const brightness = toNumber(raw.brightness);
  if (latitude === null || longitude === null || brightness === null) {
    return null;
  }

  const scan = toNumber(raw.scan);
  const track = toNumber(raw.track);
  const brightT31 = toNumber(raw.bright_t31);
  const frp = toNumber(raw.frp);
  const confidence = toNumber(raw.confidence);

  const acqDate = raw.acq_date ? String(raw.acq_date) : null;
  const acqTime = raw.acq_time ? String(raw.acq_time) : null;
  const satellite = raw.satellite ? String(raw.satellite) : null;
  const daynight = raw.daynight ? String(raw.daynight) : null;
  const version = raw.version ? String(raw.version) : null;

  if (!acqDate || !acqTime || !satellite) {
    return null;
  }

  return {
    latitude,
    longitude,
    brightness,
    scan,
    track,
    acq_date: acqDate,
    acq_time: acqTime,
    satellite,
    confidence,
    version,
    bright_t31: brightT31,
    frp,
    daynight,
  };
}

export async function replaceCountryObservations(
  countryCode: string,
  rawRows: Record<string, any>[]
): Promise<void> {
  const db = await getDatabase();
  const normalized = rawRows
    .map((row) => normalizeObservation(row))
    .filter((row): row is ObservationRecord => Boolean(row));

  db.run('BEGIN IMMEDIATE TRANSACTION');
  try {
    const deleteStatement = db.prepare(
      'DELETE FROM observations WHERE country_code = ?'
    );
    deleteStatement.bind([countryCode]);
    deleteStatement.run();
    deleteStatement.free();

    if (normalized.length) {
      const insertStatement = db.prepare(`
        INSERT OR REPLACE INTO observations (
          country_code,
          latitude,
          longitude,
          brightness,
          scan,
          track,
          acq_date,
          acq_time,
          satellite,
          confidence,
          version,
          bright_t31,
          frp,
          daynight
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `);

      for (const row of normalized) {
        insertStatement.run([
          countryCode,
          row.latitude,
          row.longitude,
          row.brightness,
          row.scan,
          row.track,
          row.acq_date,
          row.acq_time,
          row.satellite,
          row.confidence,
          row.version,
          row.bright_t31,
          row.frp,
          row.daynight,
        ]);
      }
      insertStatement.free();
    }

    db.run('COMMIT');
    try {
      persistDatabase(db);
    } catch (storageError) {
      console.warn('Failed to persist wildfire observations DB', storageError);
    }
  } catch (error) {
    db.run('ROLLBACK');
    throw error;
  }
}

function rowToFeature(row: any): Feature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [Number(row.longitude), Number(row.latitude)],
    },
    properties: {
      id: row.id,
      brightness: row.brightness,
      scan: row.scan,
      track: row.track,
      acq_date: row.acq_date,
      acq_time: row.acq_time,
      satellite: row.satellite,
      confidence: row.confidence,
      version: row.version,
      bright_t31: row.bright_t31,
      frp: row.frp,
      daynight: row.daynight,
    },
  };
}

export async function readCountriesGeoJson(
  countryCodes: string[]
): Promise<Record<string, FeatureCollection>> {
  const db = await getDatabase();
  const result: Record<string, FeatureCollection> = {};
  if (!countryCodes.length) {
    return result;
  }
  const placeholders = countryCodes.map(() => '?').join(',');
  const statement = db.prepare(
    `SELECT * FROM observations WHERE country_code IN (${placeholders})`
  );
  statement.bind(countryCodes);
  for (const code of countryCodes) {
    result[code] = { type: 'FeatureCollection', features: [] };
  }
  while (statement.step()) {
    const row = statement.getAsObject();
    const code = String(row.country_code);
    if (!result[code]) {
      result[code] = { type: 'FeatureCollection', features: [] };
    }
    (result[code].features as Feature[]).push(rowToFeature(row));
  }
  statement.free();
  return result;
}

export async function clearObservations(): Promise<void> {
  const db = await getDatabase();
  db.run('DELETE FROM observations');
  persistDatabase(db);
}

export interface ObservationMetricSummary {
  average: number | null;
  minimum: number | null;
  maximum: number | null;
}

export interface BoundingBoxObservationStats {
  count: number;
  scan: ObservationMetricSummary;
  track: ObservationMetricSummary;
  brightness: ObservationMetricSummary;
  frp: ObservationMetricSummary;
}

function extractNullableNumber(
  row: Record<string, unknown>,
  key: string
): number | null {
  const value = row[key];
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export async function countObservationsInBoundingBox(
  bounds: BoundingBox
): Promise<BoundingBoxObservationStats> {
  const db = await getDatabase();
  const { south, north, west, east } = normalizeBounds(bounds);

  let statement: ReturnType<Database['prepare']> | null = null;
  try {
    const selectClause = `SELECT
        COUNT(*) as count,
        AVG(scan) as avg_scan,
        MIN(scan) as min_scan,
        MAX(scan) as max_scan,
        AVG(track) as avg_track,
        MIN(track) as min_track,
        MAX(track) as max_track,
        AVG(brightness) as avg_brightness,
        MIN(brightness) as min_brightness,
        MAX(brightness) as max_brightness,
        AVG(frp) as avg_frp,
        MIN(frp) as min_frp,
        MAX(frp) as max_frp
      FROM observations`;

    if (east >= west) {
      statement = db.prepare(
        `${selectClause}
          WHERE latitude BETWEEN ? AND ?
            AND longitude BETWEEN ? AND ?`
      );
      statement.bind([south, north, west, east]);
    } else {
      statement = db.prepare(
        `${selectClause}
          WHERE latitude BETWEEN ? AND ?
            AND (longitude >= ? OR longitude <= ?)`
      );
      statement.bind([south, north, west, east]);
    }

    if (!statement.step()) {
      return {
        count: 0,
        scan: { average: null, minimum: null, maximum: null },
        track: { average: null, minimum: null, maximum: null },
        brightness: { average: null, minimum: null, maximum: null },
        frp: { average: null, minimum: null, maximum: null },
      };
    }
    const row = statement.getAsObject();
    const count = extractCount(row);

    return {
      count,
      scan: {
        average: extractNullableNumber(row, 'avg_scan'),
        minimum: extractNullableNumber(row, 'min_scan'),
        maximum: extractNullableNumber(row, 'max_scan'),
      },
      track: {
        average: extractNullableNumber(row, 'avg_track'),
        minimum: extractNullableNumber(row, 'min_track'),
        maximum: extractNullableNumber(row, 'max_track'),
      },
      brightness: {
        average: extractNullableNumber(row, 'avg_brightness'),
        minimum: extractNullableNumber(row, 'min_brightness'),
        maximum: extractNullableNumber(row, 'max_brightness'),
      },
      frp: {
        average: extractNullableNumber(row, 'avg_frp'),
        minimum: extractNullableNumber(row, 'min_frp'),
        maximum: extractNullableNumber(row, 'max_frp'),
      },
    };
  } finally {
    statement?.free();
  }
}

export async function runObservationScalarQuery(
  query: string
): Promise<number> {
  const trimmed = query.trim();
  if (!trimmed) {
    throw new Error('Query cannot be empty.');
  }

  const normalized = trimmed.replace(/^\uFEFF/, '');
  const startsWithSelect = normalized.toLowerCase().startsWith('select');
  if (!startsWithSelect) {
    throw new Error('Only SELECT statements are allowed.');
  }

  const db = await getDatabase();

  let resultSets;
  try {
    resultSets = db.exec(normalized);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : String(error ?? 'Unknown error');
    throw new Error(`Failed to execute query: ${message}`);
  }

  if (!resultSets.length || !resultSets[0].values.length) {
    throw new Error('The query returned no rows.');
  }

  const firstRow = resultSets[0].values[0];
  if (!firstRow || !firstRow.length) {
    throw new Error('The query returned no columns.');
  }

  const value = firstRow[0];
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new Error('The query did not return a numeric value.');
  }

  return numericValue;
}
